// src/AdminRevenueTab.js
import React, { useState, useEffect } from 'react';
import { authedFetch } from './utils/authedFetch';
import { apiUrl } from './utils/apiBase';

const AdminRevenueTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchRevenueAnalytics();
  }, []);

  const fetchRevenueAnalytics = async () => {
    try {
      setLoading(true);
      const response = await authedFetch(`${apiUrl('adminAnalytics')}?type=revenue`);
      const result = await response.json();
      
      if (result.success) {
        setAnalytics(result.data);
        setLastUpdated(new Date(result.timestamp));
        setError(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to fetch revenue analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={loadingStyle}>Loading revenue analytics...</div>;
  }

  if (error) {
    return (
      <div style={errorStyle}>
        <p>Error: {error}</p>
        <button onClick={fetchRevenueAnalytics} style={refreshButtonStyle}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2>Revenue Analytics</h2>
        <div style={refreshContainerStyle}>
          <button onClick={fetchRevenueAnalytics} style={refreshButtonStyle}>
            Refresh Data
          </button>
          {lastUpdated && (
            <span style={timestampStyle}>
              Last updated: {lastUpdated.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Key Revenue Metrics */}
      <div style={metricsGridStyle}>
        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Monthly Recurring Revenue</h3>
          <div style={metricValueStyle}>${analytics.currentMRR}</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Annual Recurring Revenue</h3>
          <div style={metricValueStyle}>${analytics.annualRecurringRevenue}</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Total Current Revenue</h3>
          <div style={metricValueStyle}>${analytics.totalCurrentRevenue}</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Average Revenue Per User</h3>
          <div style={metricValueStyle}>${analytics.arpu}</div>
        </div>
      </div>

      {/* Revenue by Plan Type */}
      <div style={sectionStyle}>
        <h3>Revenue by Plan Type</h3>
        <div style={planRevenueGridStyle}>
          <div style={planRevenueCardStyle}>
            <div style={planIconStyle}>📅</div>
            <div style={planLabelStyle}>Monthly Plans</div>
            <div style={planRevenueValueStyle}>${analytics.monthlyPlanRevenue}</div>
            <div style={planDescStyle}>from monthly subscriptions</div>
          </div>
          <div style={planRevenueCardStyle}>
            <div style={planIconStyle}>📊</div>
            <div style={planLabelStyle}>Annual Plans</div>
            <div style={planRevenueValueStyle}>${analytics.annualPlanRevenue}</div>
            <div style={planDescStyle}>from annual subscriptions</div>
          </div>
        </div>
      </div>

      {/* Revenue Projections */}
      <div style={sectionStyle}>
        <h3>Revenue Projections</h3>
        <div style={projectionGridStyle}>
          <div style={projectionCardStyle}>
            <div style={projectionLabelStyle}>Projected Annual Revenue</div>
            <div style={projectionValueStyle}>${analytics.projectedAnnualRevenue}</div>
            <div style={projectionDescStyle}>Based on current MRR trend</div>
          </div>
          <div style={projectionCardStyle}>
            <div style={projectionLabelStyle}>Upgrade Revenue Potential</div>
            <div style={projectionValueStyle}>${analytics.upgradeRevenuePotential}</div>
            <div style={projectionDescStyle}>If all monthly users upgrade to annual</div>
          </div>
        </div>
      </div>

      {/* Payment Issues */}
      <div style={sectionStyle}>
        <h3>Payment Status</h3>
        <div style={paymentStatusStyle}>
          <div style={paymentItemStyle}>
            <div style={paymentLabelStyle}>Failed Payments</div>
            <div style={failedPaymentValueStyle}>{analytics.failedPayments}</div>
          </div>
          {analytics.failedPayments > 0 && (
            <div style={alertBoxStyle}>
              <span style={alertIconStyle}>⚠️</span>
              <span>There are failed payments that need attention</span>
            </div>
          )}
        </div>
      </div>

      {/* Revenue Health Score */}
      <div style={sectionStyle}>
        <h3>Revenue Health Summary</h3>
        <div style={healthSummaryStyle}>
          <div style={healthItemStyle}>
            <span style={healthLabelStyle}>Revenue Diversification:</span>
            <span style={healthValueStyle}>
              {analytics.annualPlanRevenue > 0 ? 'Good (Mixed Plans)' : 'Fair (Monthly Only)'}
            </span>
          </div>
          <div style={healthItemStyle}>
            <span style={healthLabelStyle}>Payment Health:</span>
            <span style={{...healthValueStyle, color: analytics.failedPayments === 0 ? '#22c55e' : '#ef4444'}}>
              {analytics.failedPayments === 0 ? 'Excellent' : `${analytics.failedPayments} Issues`}
            </span>
          </div>
          <div style={healthItemStyle}>
            <span style={healthLabelStyle}>Growth Potential:</span>
            <span style={healthValueStyle}>
              ${analytics.upgradeRevenuePotential} available from upgrades
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  padding: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '2rem',
  paddingBottom: '1rem',
  borderBottom: '2px solid #e5e7eb'
};

const refreshContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem'
};

const refreshButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#1976d2',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer'
};

const timestampStyle = {
  fontSize: '0.9rem',
  color: '#666'
};

const metricsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '1.5rem',
  marginBottom: '3rem'
};

const metricCardStyle = {
  backgroundColor: 'white',
  padding: '1.5rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  textAlign: 'center'
};

const metricTitleStyle = {
  margin: '0 0 1rem 0',
  fontSize: '0.9rem',
  fontWeight: '600',
  color: '#666',
  textTransform: 'uppercase'
};

const metricValueStyle = {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  color: '#22c55e'
};

const sectionStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  marginBottom: '2rem'
};

const planRevenueGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2rem',
  marginTop: '1rem'
};

const planRevenueCardStyle = {
  textAlign: 'center',
  padding: '1.5rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '10px'
};

const planIconStyle = {
  fontSize: '2rem',
  marginBottom: '0.5rem'
};

const planLabelStyle = {
  fontSize: '1.1rem',
  fontWeight: '600',
  marginBottom: '0.5rem'
};

const planRevenueValueStyle = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#22c55e',
  marginBottom: '0.5rem'
};

const planDescStyle = {
  fontSize: '0.9rem',
  color: '#666'
};

const projectionGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2rem',
  marginTop: '1rem'
};

const projectionCardStyle = {
  padding: '1.5rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  textAlign: 'center'
};

const projectionLabelStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#666',
  marginBottom: '0.5rem'
};

const projectionValueStyle = {
  fontSize: '1.8rem',
  fontWeight: 'bold',
  color: '#1976d2',
  marginBottom: '0.5rem'
};

const projectionDescStyle = {
  fontSize: '0.85rem',
  color: '#666'
};

const paymentStatusStyle = {
  marginTop: '1rem'
};

const paymentItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  marginBottom: '1rem'
};

const paymentLabelStyle = {
  fontSize: '1.1rem',
  fontWeight: '600'
};

const failedPaymentValueStyle = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#ef4444'
};

const alertBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '1rem',
  backgroundColor: '#fef3c7',
  border: '1px solid #f59e0b',
  borderRadius: '8px',
  color: '#92400e'
};

const alertIconStyle = {
  fontSize: '1.2rem'
};

const healthSummaryStyle = {
  marginTop: '1rem'
};

const healthItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.75rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '5px',
  marginBottom: '0.5rem'
};

const healthLabelStyle = {
  fontSize: '1rem',
  fontWeight: '600'
};

const healthValueStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#1976d2'
};

const loadingStyle = {
  padding: '2rem',
  textAlign: 'center',
  fontSize: '1.2rem'
};

const errorStyle = {
  padding: '2rem',
  textAlign: 'center',
  color: '#d32f2f'
};

export default AdminRevenueTab;